import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils'

interface Props {
  id: string
  name: string
  label?: string
  items?: string[]
  disabled?: boolean
  className?: string
  onChange?: (value: string) => void
}

export const Dropdown = ({
  id,
  name,
  label,
  items = ['Item 1', 'Item 2', 'Item 3'],
  disabled = false,
  className,
  onChange,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<string>('Select an item')

  const handleSelect = (item: string) => {
    setSelectedItem(item)
    setIsOpen(false)
    onChange?.(item)
  }

  return (
    <div className="relative w-full">
      {label && (
        <label className={`mb-2 block text-sm font-medium ${disabled ? 'text-gray-400' : 'text-darkblue-900'}`}>
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        value={selectedItem}
        id={id}
        name={name}
        className={cn(
          'relative w-full rounded-md border px-4 py-2 text-left',
          !disabled && 'focus:outline-none focus:ring-2 focus:ring-darkgreen',
          {
            'cursor-not-allowed bg-gray-100': disabled,
            'border-darkgreen': isOpen,
            'border-gray-300 hover:border-darkgreen': !disabled && !isOpen,
          },
          className
        )}
      >
        <span
          className={cn('block truncate', {
            'text-gray-500' : !disabled,
          })}
        >
          {selectedItem}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
          <ChevronDown
            className={cn('h-4 w-4 text-gray-400', {
              'text-gray-500': disabled,
            })}
          />
        </span>
      </button>

      {isOpen && (
        <ul className="absolute z-10 mt-3 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg border-[1.5px] ring-1 ring-black ring-opacity-5 focus:outline-none">
          {items.map((item, index) => (
            <li
              key={index}
              onClick={() => handleSelect(item)}
              className={cn(
                'relative cursor-pointer select-none py-2 pl-3 pr-9',
                'hover:bg-darkgreen hover:text-white',
                {
                  'text-gray-500': item !== selectedItem,
                  'text-darkgreen': item === selectedItem,
                }
              )}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}